/**
 * Picking a block of a gridded 3D model without walking every instance (three.js raycasting is O(n)): the ray is
 * taken into the model's own horizontal frame and marched column by column through the cell grid (a 2D DDA over
 * the grid edges), testing only the cells of each column against the ray. Pure, no three.js.
 */
import { buildGrid } from './volume-slices';
import { modelTheta } from './voxel-select';

/**
 * Pick grid of a selection's valid cells placed at `positions` (scene x east, y up, z north; voxel-select
 * flatPositions or the terrain-draped ones). Same rotation and anchor as blockMatrices. Null when the cells are not on a grid.
 */
export function buildPickGrid(cells, positions) {
  const grid = buildGrid(cells);
  if (!grid || !positions?.length) return null;
  const th = modelTheta(cells, positions);
  const half = new Float32Array(cells.length);
  let yLo = Infinity, yHi = -Infinity;
  for (let i = 0; i < cells.length; i++) {
    half[i] = Math.abs(cells[i].bottom - cells[i].top) / 2;
    const y = positions[i * 3 + 1];
    if (y - half[i] < yLo) yLo = y - half[i];
    if (y + half[i] > yHi) yHi = y + half[i];
  }
  return { grid, positions, half, yLo, yHi, ax: cells[0].x, ay: cells[0].y, px: positions[0], pz: positions[2], cs: Math.cos(th), sn: Math.sin(th) };
}

/** [t0, t1] of the ray inside [lo, hi] along one axis, intersected with [a, b]; null when empty. */
function slab(o, d, lo, hi, a, b) {
  if (Math.abs(d) < 1e-12) return o >= lo && o <= hi ? [a, b] : null;
  let t1 = (lo - o) / d, t2 = (hi - o) / d;
  if (t1 > t2) { const s = t1; t1 = t2; t2 = s; }
  const s = Math.max(a, t1), e = Math.min(b, t2);
  return s <= e ? [s, e] : null;
}

/** Index of the interval of sorted `edges` holding v (clamped to the grid). */
function interval(edges, v) {
  const n = edges.length - 1;
  let a = 0, b = n - 1;
  while (a < b) { const m = (a + b + 1) >> 1; if (edges[m] <= v) a = m; else b = m - 1; }
  return a;
}

/**
 * First visible cell along a ray.
 * @param {{origin:number[], dir:number[]}} ray scene frame (dir need not be unit; t is in units of dir)
 * @param {object} pick buildPickGrid result
 * @param {ArrayLike<number>} visibleMask per cell, truthy = can be picked (drawn: alpha > 0 and passing the filters)
 * @returns {{ index:number, t:number, point:number[] } | null}
 */
export function pickCell(ray, pick, visibleMask) {
  if (!pick || !ray) return null;
  const { grid, positions, half, yLo, yHi, ax, ay, px, pz, cs, sn } = pick;
  const { x: ex, y: ey } = grid.edges;
  const { nx, ny, nd, lookup } = grid;
  const [ox, oy, oz] = ray.origin, [dx, dy, dz] = ray.dir;
  // scene -> model horizontal frame (inverse of the block rotation)
  const rx = ox - px, rz = oz - pz;
  const mox = ax + rx * cs + rz * sn, moy = ay - rx * sn + rz * cs;
  const mdx = dx * cs + dz * sn, mdy = -dx * sn + dz * cs;
  let w = slab(mox, mdx, ex[0], ex[nx], 0, Infinity);
  if (w) w = slab(moy, mdy, ey[0], ey[ny], w[0], w[1]);
  if (w) w = slab(oy, dy, yLo, yHi, w[0], w[1]);
  if (!w) return null;
  const tEnd = w[1];
  let t = w[0];
  let ix = interval(ex, mox + mdx * t), iy = interval(ey, moy + mdy * t);
  for (let guard = 0; guard < nx + ny + 4; guard++) {
    const tx = mdx > 0 ? (ex[ix + 1] - mox) / mdx : mdx < 0 ? (ex[ix] - mox) / mdx : Infinity;
    const ty = mdy > 0 ? (ey[iy + 1] - moy) / mdy : mdy < 0 ? (ey[iy] - moy) / mdy : Infinity;
    const tn = Math.min(tx, ty, tEnd);
    // cells of this column that the ray crosses within [t, tn]; the nearest wins
    let best = -1, bestT = Infinity;
    for (let id = 0; id < nd; id++) {
      const c = lookup.get(ix + nx * (iy + ny * id));
      if (c === undefined || !visibleMask[c]) continue;
      const yc = positions[c * 3 + 1];
      const s = slab(oy, dy, yc - half[c], yc + half[c], t, tn);
      if (s && s[0] < bestT) { bestT = s[0]; best = c; }
    }
    if (best >= 0) return { index: best, t: bestT, point: [ox + dx * bestT, oy + dy * bestT, oz + dz * bestT] };
    if (tn >= tEnd) return null;
    if (tx <= tn) ix += mdx > 0 ? 1 : -1;
    if (ty <= tn) iy += mdy > 0 ? 1 : -1;
    if (ix < 0 || ix >= nx || iy < 0 || iy >= ny) return null;
    t = tn;
  }
  return null;
}
